/**
 * Environment Variable Validation
 * Comprehensive validation for all required environment variables
 */

interface EnvConfig {
  // Stripe (Frontend only)
  VITE_STRIPE_PUBLIC_KEY: string
  
  // Site
  VITE_SITE_URL: string
  
  // API
  VITE_API_URL: string
  
  // Email (Optional)
  VITE_EMAIL_FROM?: string
  VITE_EMAIL_REPLY_TO?: string
}

interface ValidationResult {
  isValid: boolean
  missing: string[]
  warnings: string[]
  config: Partial<EnvConfig>
}

/**
 * Validates all required environment variables
 */
export function validateEnvironment(): ValidationResult {
  const missing: string[] = []
  const warnings: string[] = []
  const config: Partial<EnvConfig> = {}


  // Required Stripe variables (Frontend only)
  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY

  if (!stripePublicKey) {
    missing.push('VITE_STRIPE_PUBLIC_KEY')
  } else {
    config.VITE_STRIPE_PUBLIC_KEY = stripePublicKey
    // Validate key format
    if (!stripePublicKey.startsWith('pk_')) {
      warnings.push('VITE_STRIPE_PUBLIC_KEY appears to be invalid (should start with pk_)')
    }
  }

  // Required Site URL
  const siteUrl = import.meta.env.VITE_SITE_URL
  if (!siteUrl) {
    missing.push('VITE_SITE_URL')
  } else {
    config.VITE_SITE_URL = siteUrl
    // Validate URL format
    try {
      new URL(siteUrl)
    } catch {
      warnings.push('VITE_SITE_URL appears to be invalid')
    }
  }

  // Required API URL
  const apiUrl = import.meta.env.VITE_API_URL
  if (!apiUrl) {
    missing.push('VITE_API_URL')
  } else {
    config.VITE_API_URL = apiUrl
    // Validate URL format
    try {
      new URL(apiUrl)
    } catch {
      warnings.push('VITE_API_URL appears to be invalid')
    }
  }

  // Optional Email variables
  const emailFrom = import.meta.env.VITE_EMAIL_FROM
  const emailReplyTo = import.meta.env.VITE_EMAIL_REPLY_TO

  if (emailFrom) {
    config.VITE_EMAIL_FROM = emailFrom
    // Basic email validation
    if (!emailFrom.includes('@')) {
      warnings.push('VITE_EMAIL_FROM appears to be invalid')
    }
  }

  if (emailReplyTo) {
    config.VITE_EMAIL_REPLY_TO = emailReplyTo
    // Basic email validation
    if (!emailReplyTo.includes('@')) {
      warnings.push('VITE_EMAIL_REPLY_TO appears to be invalid')
    }
  }

  return {
    isValid: missing.length === 0,
    missing,
    warnings,
    config
  }
}

/**
 * Validates environment variables and throws error if critical ones are missing
 */
export function validateEnvironmentOrThrow(): EnvConfig {
  const result = validateEnvironment()
  
  if (!result.isValid) {
    const errorMessage = [
      'Missing required environment variables:',
      ...result.missing.map(key => `  - ${key}`),
      '',
      'Please check your .env.local file and ensure all required variables are set.',
      'See README.md for the complete list of required variables.'
    ].join('\n')
    
    throw new Error(errorMessage)
  }

  if (result.warnings.length > 0) {
    console.warn('Environment validation warnings:')
    result.warnings.forEach(warning => console.warn(`  - ${warning}`))
  }

  return result.config as EnvConfig
}

/**
 * Get validated environment configuration
 * Use this in your application instead of direct process.env access
 */
export function getValidatedEnv(): EnvConfig {
  return validateEnvironmentOrThrow()
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.MODE === 'development'
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.MODE === 'production'
}
